/**
 * PostsSummary.jsx
 * Paginated list of post summaries, styled for the executive theme.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesRight, faAnglesLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import {useState, useCallback, useEffect} from 'react';

import {useNavHelper} from '#hooks/useNavHelper';
import { scaleAndCoverImage } from '#modules/scaleAndCoverImage';
import { APIRequest } from '#modules/APIRequest';
import config from '#config/server';

export function PostsSummary({searchQuery='', noResultsText='', tags=[]}){

	const navigate = useNavHelper();
	let [results, setResults] = useState([]);
	let [page, setPage] = useState(1);
	let [totalPages, setTotalPages] = useState(0);

	const generateThumb = useCallback(node=>{
		if(node){
			scaleAndCoverImage(node.dataset.src, 700, 500).then(src=>{ node.src = src; });
		}
	});

	const getRows = async ()=>{
		let res;
		if(tags.length){
			res = await new APIRequest('Pagination').get({query:'all_posts_of_tag', params: JSON.stringify({tags}), page, order_by_col:'create_ts', order_dir:'desc', page_size:15, search_term:searchQuery});
		}else{
			res = await new APIRequest('Pagination').get({query:'all_posts_summary', page, order_by_col:'create_ts', order_dir:'desc', page_size:15, search_term:searchQuery});
		}
		if(!res.has_error){
			setTotalPages(res.data.total_pages);
			setResults(res.data.results);
		}
	};

	useEffect(()=>{ getRows(); }, [page, searchQuery]);

	let pagination = '';
	if(totalPages > 1){
		let p_btns = [];
		for(let i=0; i<totalPages; i++){
			let curr_page = i+1;
			if(curr_page == 1 && page !== 1){
				p_btns.push(<li key={`page-btn-${curr_page}`} className="page-item"><a className="page-link" href="#" onClick={e=>{e.preventDefault(); setPage(1);}}><FontAwesomeIcon icon={faAnglesLeft} /></a></li>);
			}
			if(curr_page == page - 1 && page > 2){
				p_btns.push(<li key={`page-btn-${curr_page}`} className="page-item"><a className="page-link" href="#" onClick={e=>{e.preventDefault(); setPage(curr_page);}}>{curr_page}</a></li>);
			}
			if(curr_page == page){
				p_btns.push(<li key={`page-btn-${curr_page}`} className="page-item"><a className="page-link disabled" href="#" onClick={e=>{e.preventDefault();}}>{curr_page}</a></li>);
			}
			if(curr_page == page + 1 && curr_page < totalPages){
				p_btns.push(<li key={`page-btn-${curr_page}`} className="page-item"><a className="page-link" href="#" onClick={e=>{e.preventDefault(); setPage(curr_page);}}>{curr_page}</a></li>);
			}
			if(curr_page == totalPages && page !== totalPages){
				p_btns.push(<li key={`page-btn-${curr_page}`} className="page-item"><a className="page-link" href="#" onClick={e=>{e.preventDefault(); setPage(curr_page);}}><FontAwesomeIcon icon={faAnglesRight} /></a></li>);
			}
		}
		pagination = (<div className='clearfix'><nav className='float-end'><ul className="pagination">{p_btns}</ul></nav></div>);
	}

	return (<>
		{results.map((post, idx)=>{
			const meta = (<small style={{display:'block', marginBottom:'.4rem'}}>{post.author_name} · {new Date(post.create_ts * 1000).toLocaleDateString('en-US', {year:'numeric', month:'long', day:'numeric'})}</small>);
			const title = (<h3 onClick={e=>navigate(e, `/post/${post.slug}`)} className="post-title-link h4">{post.title}</h3>);

			if(post.graph_img){
				const graphic = (<div className="col-sm-12 col-lg-5">
					<img onClick={e=>navigate(e, `/post/${post.slug}`)} ref={generateThumb} src={`${config.base_url}assets/images/loading-bar-animated-gif-transparent-background-3.gif`} data-src={post.graph_img} style={{cursor:'pointer', width:'100%'}} className="d-block img-fluid thumb-img" />
				</div>);
				const summary = (<div className="col-lg-7">
					{title}
					{meta}
					{!!post.summary && (<p className="mb-2">{post.summary}</p>)}
					<a href="#" onClick={e=>navigate(e, `/post/${post.slug}`)}>Read more <FontAwesomeIcon icon={faArrowRight} /></a>
				</div>);
				return (<div key={post.id} className={`row align-items-center g-4 post-summary-row ${idx % 2 === 1 ? 'flex-lg-row-reverse' : ''}`}>
					{graphic}{summary}
				</div>);
			}
			return (<div key={post.id} className="post-summary-row">
				{title}
				{meta}
				{!!post.summary && (<p className="mb-2">{post.summary}</p>)}
				<a href="#" onClick={e=>navigate(e, `/post/${post.slug}`)}>Read more <FontAwesomeIcon icon={faArrowRight} /></a>
			</div>);
		})}
		{results.length === 0 && !!noResultsText && (<p className="writing-empty">{noResultsText}</p>)}
		{pagination}
	</>);
}
